/**
 * MatchedOutlets.tsx — Confirmed outlet matches.
 *
 * Rebuilt in Pass 3 using the component library.
 * Before: 297 lines with inline table, inline filters, inline pagination.
 * After:  ~180 lines. Composition of <PageHeader>, <Table>, <Pagination>, etc.
 *
 * Preserves all behavior:
 *   - Server-side pagination (25/page)
 *   - Search with 300ms debounce
 *   - City + outlet format + client filters
 *   - Admin-only client filter + client column
 *   - Excel export with captureEvent analytics
 *   - Navigates to /outlet/:censusId on census ID click
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import { captureEvent } from '../../analytics/posthog';
import {
  PageHeader,
  Button, Icon,
  SearchInput, Select,
  Table, TableToolbar, Pagination,
  MethodTag, ConfidenceTag,
  EmptyState,
  toast,
} from '../../components/ui';

interface MatchedOutlet {
  id: string;
  census_id: string;
  client_name: string;
  raw_name: string;
  match_method: string;
  match_confidence: number | null;
  canonical_name: string;
  canonical_address: string;
  canonical_city: string;
  country: string;
}

interface RegistryFilters {
  regions: string[];
  countries: string[];
  outlet_formats: string[];
  chains: string[];
}

interface MatchedResponse {
  items: MatchedOutlet[];
  total: number;
  page: number;
  pages: number;
}

const LIMIT = 25;

export default function MatchedOutlets() {
  const navigate = useNavigate();
  const { adminMode, modeVersion } = useAdmin();

  const [data, setData] = useState<MatchedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [searchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [client, setClient] = useState(() => searchParams.get('client') || '');

  const [clients, setClients] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'Matched Outlets | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load client list (admin only)
  useEffect(() => {
    if (!adminMode) return;
    api.get<string[]>('/api/clients').then(r => setClients(r.data || [])).catch(() => {});
  }, [adminMode, modeVersion]);

  // Country dropdown options — data-driven from the registry's distinct
  // country values. With all outlets currently SERBIA, this dropdown shows
  // SERBIA as the only option (by design).
  useEffect(() => {
    api.get<RegistryFilters>('/api/registry/filters')
      .then(r => setCountries(r.data.countries || []))
      .catch(() => {});
  }, [modeVersion]);

  // Fetch table data
  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (search) params.set('search', search);
      if (country) params.set('country', country);
      if (client) params.set('client', client);
      const res = await api.get<MatchedResponse>(`/api/matched-outlets?${params}`, { signal });
      setData(res.data);
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'CanceledError' || ('code' in err && (err as { code?: string }).code === 'ERR_CANCELED'))) return;
      toast.error('Failed to load matched outlets', 'Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [page, search, country, client]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData, modeVersion]);

  // Debounced search
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val.trim()); setPage(1); }, 300);
  };

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (country) params.set('country', country);
      if (client) params.set('client', client);

      const res = await api.get(`/api/matched-outlets/export?${params}`, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = `matched_outlets_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(href);
      captureEvent('export_downloaded', { source: 'matched_outlets', filters: { search, country, client } });
      toast.success('Export ready', `${(data?.total ?? 0).toLocaleString()} rows downloaded`);
    } catch {
      toast.error('Export failed', 'Please try again or contact support.');
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  // Uniform columns across admin / demo / client modes — see PROMPT 3 spec.
  const columns = [
    {
      key: 'census_id',
      header: 'Census ID',
      mono: true,
      width: 130,
      render: (r: MatchedOutlet) => (
        <span
          onClick={(e) => { e.stopPropagation(); navigate(`/outlet/${r.census_id}`); }}
          style={{
            color: theme.brand.cyanLight,
            cursor: 'pointer',
            fontFamily: theme.font.data,
            fontSize: theme.fontSize.sm,
            transition: `color ${theme.motion.fast}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = theme.brand.cyan; e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={e => { e.currentTarget.style.color = theme.brand.cyanLight; e.currentTarget.style.textDecoration = 'none'; }}
        >
          {r.census_id}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Outlet Name',
      render: (r: MatchedOutlet) => r.canonical_name || r.raw_name || null,
    },
    {
      key: 'address',
      header: 'Address',
      render: (r: MatchedOutlet) => (
        <span style={{ color: theme.text.secondary }}>{r.canonical_address || '—'}</span>
      ),
    },
    {
      key: 'city',
      header: 'City',
      render: (r: MatchedOutlet) => (
        <span style={{ color: theme.text.secondary }}>{r.canonical_city || '—'}</span>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      render: (r: MatchedOutlet) => (
        <span style={{ color: theme.text.secondary }}>{r.country || '—'}</span>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      render: (r: MatchedOutlet) => <MethodTag method={r.match_method} />,
      nowrap: true,
    },
    {
      key: 'confidence',
      header: 'Confidence',
      align: 'right' as const,
      render: (r: MatchedOutlet) => <ConfidenceTag score={r.match_confidence} />,
      nowrap: true,
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Matched Outlets"
        description="Confirmed outlet matches across your registry."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {total.toLocaleString()}
            </span>
            <span>confirmed matches</span>
          </>
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<Icon.Download size={14} />}
            onClick={handleExport}
            loading={exporting}
          >
            Export Excel
          </Button>
        }
      />

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: theme.space[3],
        marginBottom: theme.space[5],
      }}>
        <SearchInput
          placeholder="Search name or address…"
          value={searchInput}
          onChange={e => handleSearchInput(e.target.value)}
          kbd={undefined}
        />
        <Select
          value={country}
          onChange={e => { setCountry(e.target.value); setPage(1); }}
        >
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        {adminMode && clients.length > 0 && (
          <Select
            value={client}
            onChange={e => { setClient(e.target.value); setPage(1); }}
          >
            <option value="">All clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
      </div>

      {/* Toolbar: count + pagination */}
      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {Math.min(LIMIT, items.length).toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={pages} onChange={setPage} />
      </TableToolbar>

      {/* Table */}
      <Table
        data={items}
        columns={columns}
        loading={loading}
        minWidth={900}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.Search size={18} />}
            title="No matched outlets found"
            description="Try clearing filters or adjusting your search."
            action={(search || country || client) ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearchInput(''); setSearch('');
                  setCountry(''); setClient('');
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            ) : undefined}
          />
        }
      />
    </div>
  );
}
